import LiveEventModule from '@/store/model/LiveEventModule'
import store from '@/store'
import { getModule } from 'vuex-module-decorators'
import moment from 'moment'
import Api from './Api'
import SelectOption from './interface/SelectOption'

export default class LiveEvent {
  public id: string = ''

  public league: string = ''

  public name: string = ''

  public network_ids: string[] = []

  public teams: string[] = []

  public created_at: string = ''

  public updated_at: string = ''

  constructor(data: any) {
    this.id = data.id
    this.league = data.league
    this.name = data.name
    this.network_ids = data.network_ids
    this.teams = data.teams
    this.created_at = data.created_at
    this.updated_at = data.updated_at
  }

  public static toObject(data: any, cache: boolean = true): LiveEvent {
    const event = new LiveEvent(data)

    //  Cache Object
    if (cache) LiveEvent.module.update(event)

    return event
  }

  public static toObjectList(data: any, cache: boolean = true): LiveEvent[] {
    const events = new Array<LiveEvent>()
    data.forEach((value: any) => {
      const event = LiveEvent.toObject(value, false)
      events.push(event)
    })

    //  Cache Object
    if (cache) LiveEvent.module.update(events)

    return events
  }

  /// State Management
  public static get module(): LiveEventModule {
    if (!store.hasModule('live_event')) {
      store.registerModule('live_event', LiveEventModule)
    }

    return getModule(LiveEventModule)
  }

  public static find(id: string): LiveEvent | null {
    const o = LiveEvent.module.data.find(event => event.id === id)
    return o instanceof LiveEvent ? o : null
  }

  public static getAllByLeague(
    league: string,
    network_ids: string[],
    teams: string[],
  ): LiveEvent[] {
    const events = LiveEvent.module.data
    const list = events.filter(
      (event: LiveEvent) =>
        event.league === league
        && (network_ids.length === 0 || network_ids.some(n => event.network_ids.includes(n)))
        && (teams.length === 0 || teams.some(t => event.teams.includes(t))),
    )

    return list
  }

  public static getTeamsByLeagueAndNetwork(league: string, network_ids: string[]): SelectOption[] {
    let teams: string[] = []
    LiveEvent.getAllByLeague(league, network_ids, [])
      .sort((a, b) => moment(a.created_at).diff(moment(b.created_at)))
      .forEach(
        (event: LiveEvent) => {
          console.log(event)
          event.teams.forEach((team: string) => {
            if (!teams.includes(team)) {
              teams.push(team)
            }
          })
        },
      )

    return teams
      .sort((a, b) => (a > b ? 1 : b > a ? -1 : 0))
      .map((team: string) => new SelectOption(team, team))
  }

  public static getOptionsByLeague(
    league: string,
    network_ids: string[],
    teams: string[],
  ): SelectOption[] {
    return LiveEvent.getAllByLeague(league, network_ids, teams)
      .sort((a, b) => moment(a.created_at).diff(moment(b.created_at)))
      .map(
        (event: LiveEvent) =>
          new SelectOption(
            `[${moment(event.created_at).format('MM/DD/YY h:mm a')}] ${event.name}`,
            event.id,
          ),
      )
  }

  public static getNetworkOptions(league: string): SelectOption[] {
    const network_ids: string[] = []
    LiveEvent.getAllByLeague(league, [], []).forEach((event: LiveEvent) => {
      event.network_ids.forEach((network: string) => {
        if (!network_ids.includes(network)) {
          network_ids.push(network)
        }
      })
    })

    return network_ids
      .sort((a, b) => (a > b ? 1 : b > a ? -1 : 0))
      .map((network: string) => new SelectOption(network, network))
  }

  public static fetchAllValidEvents(): Promise<LiveEvent[]> {
    const api = new Api()
    return api
      .get('live_events/valid')
      .then(response => LiveEvent.toObjectList(response.data.result.live_events))
  }

  public static async searchOptions(query: any) {
    const api = new Api()

    return api
      .get('live_event/search/option', query)
      .then(response => SelectOption.toObjectList(response.data.result.options))
      .catch(() => [])
  }

  /**
   * Fetches filter options for report builder based on the type (league or network).
   * It caches the results in the LiveEvent module.
   *
   *  - VUEX
   *  LiveEvent.module {
   *  ...
   *  leagues: [],
   *  networks: [],
   *  ...
   * }
   *
   * @param {Object} settings - The settings object containing query, local_filter, search_backend, and type.
   * @param {Object} settings.query - The query parameters for the API request.
   * @param {Object} settings.query.search - The query parameters for the API request.
   * @param {Function} settings.local_filter - A function to filter the results locally.
   * @param {boolean} settings.search_backend - A flag indicating whether to request and search the backend if the searched item is not found in local cache/variable.
   * @param {string} settings.type - The type of options to fetch ('league' or 'network').
   * @returns {Promise<Array<SelectOption>>} - A promise that resolves to an array of SelectOption objects.
   * @ref use this as reference for other report builder filter options
   */
  public static async report_builder_filter_options({
    query,
    local_filter,
    search_backend,
    type,
  }: {
    query: any
    local_filter: (option: SelectOption) => boolean
    search_backend?: boolean
    type: 'league' | 'network'
  }): Promise<Array<SelectOption>> {
    let _search_backend = false
    if (search_backend) _search_backend = search_backend

    let load = async () => {
      const api = new Api()

      let response = api
        .get(`live_events/network-leagues/${type}`, query)
        .then(response => response.data.result)

      return response.then((list: any) => {
        let formatted = list.map((l: any) => {
          l.name = l.name.replace(/_/g, ' ').toUpperCase()
          let options = new SelectOption(l.name, l.value)
          options.type = type
          return options
        })

        if (type === 'league') {
          LiveEvent.module.concatLeagues(formatted)
        } else if (type === 'network') {
          LiveEvent.module.concatNetworks(formatted)
        }

        // @ts-ignore
        return LiveEvent.module[`${type}s`]
      })
    }

    let readCache = (reload: boolean = false) => {
      // @ts-ignore
      let found = LiveEvent.module[`${type}s`]

      if (query && query.search && query.search.length) {
        found = found.filter(local_filter)
      }

      if (!reload) return found

      if (!found.length && query && query.search && query.search.length) {
        return load().then(loaded => {
          if (query && query.search && query.search.length) {
            return loaded.filter(local_filter)
          }
          return loaded
        })
      }

      return found
    }

    // @ts-ignore
    if (LiveEvent.module[`${type}s`].length === 0) {
      return load()
    }
    return readCache(_search_backend)
  }

  public static reportBuilderOptions = {
    league: (settings: any) =>
      LiveEvent.report_builder_filter_options({ ...settings, type: 'league' }),
    network: (settings: any) =>
      LiveEvent.report_builder_filter_options({ ...settings, type: 'network' }),
  }
}
